"use client";

import React from "react";
import { Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function AccessFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-fade-in-up">
        <div className="bg-green-100 text-green-600 p-3 rounded-full mb-4">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-display font-bold text-foreground mb-2">Request Received</h3>
        <p className="text-muted-foreground mb-6 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="hover-lift">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="companyName" rules={{
        required: "Company name is required",
        minLength: {
          value: 2,
          message: "Must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Company Name</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Acme Corporation" {...field} className="bg-white/50 focus:bg-white transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="contactPerson" rules={{
        required: "Contact person is required",
        minLength: {
          value: 2,
          message: "Must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Contact Person</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="bg-white/50 focus:bg-white transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Business Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@acmecorp.com" {...field} className="bg-white/50 focus:bg-white transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className="bg-white/50 focus:bg-white transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel>Additional Information (Optional)</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your specific needs or requirements..." className="min-h-[120px] bg-white/50 focus:bg-white transition-colors resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-md flex items-start gap-3 text-red-800 animate-fade-in-up">
          <CircleAlert className="size-5 shrink-0 mt-0.5" />
          <p className="text-sm">{submitError}</p>
        </div>}

      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto bg-accent text-white hover:bg-accent-hover active:bg-accent-active px-8 py-6 text-base font-display font-semibold shadow-soft hover:shadow-medium transition-all duration-200">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Submitting Request...
            </> : 'Request Portal Access'}
        </Button>
      </div>
    </Form>;
}
export default function RequestAccessForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="request-access-form" className="py-20 md:py-32 bg-background relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-secondary/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={ref} className={`max-w-4xl mx-auto transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
              Request Portal Access
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Join our exclusive network. Fill out the form below to request access to our dedicated buyer and supplier portals.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-medium border border-border/50 p-6 md:p-10">
            <FormProvider formId="request_access_form_supplierandbuyerportals" sectionName="Request Access Form" defaultValues={{
            companyName: "",
            contactPerson: "",
            email: "",
            phone: "",
            message: ""
          }} validationRules={{
            companyName: {
              required: true,
              minLength: 2
            },
            contactPerson: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true
            }
          }} submitText="Request Portal Access" successMessage="Thank you for your interest. Your access request has been successfully submitted and is currently under review by our team.">
              <AccessFormInner />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}